import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calendar } from '../components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { BookingStatusBadge } from '../components/BookingStatusBadge';
import { GuestFormsDialog } from '../components/GuestFormsDialog';
import { STATUS_META, STATUS_ORDER } from '../lib/bookingStatus';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AdminCalendarView } from '../components/AdminCalendarView';
import { CancelBookingModal } from '../components/CancelBookingModal';
import { BulkDeleteBar, BulkSelectCheckbox, BulkSelectAllCheckbox } from '../components/BulkDelete';
import { Pagination } from '../components/Pagination';
import { EditBookingDialog } from '../components/EditBookingDialog';
import { BookingPaymentsPanel } from '../components/BookingPaymentsPanel';
import { ManualBookingDialog } from '../components/ManualBookingDialog';
import { CustomerMessageDialog } from '../components/CustomerMessageDialog';
import { PaymentModal } from '../components/PaymentModal';
import { ActionMenu } from '../components/crm/CrmListShell';
import useMessageStream from '../hooks/useMessageStream';
import { 
  Calendar as CalendarIcon, 
  List, 
  DollarSign, 
  Users, 
  Clock,
  AlertTriangle,
  Download,
  Mail,
  MessageSquare,
  Eye,
  Search,
  RefreshCw,
  CalendarDays,
  Ban,
  ChevronDown,
  ChevronRight,
  Pencil,
  Plus,
  FileText,
  Trash2,
  Wallet,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // When rendered inside /crm, the CrmLayout already provides the header + sidebar.
  const insideCrmLayout = location.pathname.startsWith('/crm');
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasCrmAccess, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState(() => {
    const v = searchParams.get('view');
    // Calendar is the default landing view on the Bookings page.
    // Honour ?view=byDate|list|calendar|allergies when explicitly set.
    if (v === 'byDate' || v === 'list' || v === 'calendar' || v === 'allergies') return v;
    if (v === 'fullcalendar') return 'fullcalendar';
    return 'fullcalendar';
  });
  const [bookings, setBookings] = useState([]);
  const [waiverMap, setWaiverMap] = useState({});
  const [waiverModalBookingId, setWaiverModalBookingId] = useState(null);
  const [manualBookingOpen, setManualBookingOpen] = useState(false);
  const [posOpen, setPosOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [allergyReports, setAllergyReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [savingBookedBy, setSavingBookedBy] = useState(false);
  const [bulkSelected, setBulkSelected] = useState([]);
  const [editTarget, setEditTarget] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  // Customer message dialog (Email/SMS) launched from booking row
  const [msgBookingOpen, setMsgBookingOpen] = useState(false);
  const [msgBooking, setMsgBooking] = useState(null);
  const [msgBookingChannel, setMsgBookingChannel] = useState('email');
  // Pagination for bookings list
  const [bookingsPage, setBookingsPage] = useState(1);
  const [bookingsPageSize, setBookingsPageSize] = useState(25);

  const openBookingMessage = (booking, channel = 'email') => {
    setMsgBooking(booking);
    setMsgBookingChannel(channel);
    setMsgBookingOpen(true);
  };
  const [packages, setPackages] = useState([]);

  // Filters — seed from URL params so CrmOverview card clicks drill-through work out of the box
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [lifecycleFilter, setLifecycleFilter] = useState(searchParams.get('lifecycle_status') || 'all');
  const [paymentFilter, setPaymentFilter] = useState(searchParams.get('payment_status') || 'all');
  const [packageFilter, setPackageFilter] = useState(searchParams.get('package_id') || 'all');
  const [dateFilter, setDateFilter] = useState(
    searchParams.get('date_from') ? new Date(searchParams.get('date_from')) : null,
  );
  const [searchTerm, setSearchTerm] = useState('');
  // Extra boolean filters (URL-driven only — no dropdown)
  const [outstandingOnly, setOutstandingOnly] = useState(searchParams.get('outstanding') === 'true');
  const [balanceDueOnly, setBalanceDueOnly] = useState(searchParams.get('balance_due') === 'true');

  // List View accordion — which dates are expanded
  const [expandedDates, setExpandedDates] = useState(new Set());

  useEffect(() => {
    if (!authLoading && !hasCrmAccess) {
      navigate('/');
      return;
    }
    if (hasCrmAccess) {
      fetchData();
      fetchEmployees();
      axios.get(`${API_URL}/api/packages`).then((r) => setPackages(r.data || [])).catch(() => {});
    }
  }, [hasCrmAccess, authLoading, navigate]);

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/employees`, { withCredentials: true });
      setEmployees(Array.isArray(res.data) ? res.data : (res.data?.employees || []));
    } catch (error) {
      // non-blocking
    }
  };

  const updateBookedBy = async (bookingId, employeeId) => {
    // Pre-flight conflict check when assigning a real employee (not "self")
    if (employeeId) {
      try {
        const c = await axios.get(
          `${API_URL}/api/admin/bookings/${bookingId}/assignment-conflicts`,
          { params: { employee_id: employeeId }, withCredentials: true },
        );
        const conflicts = c.data?.conflicts || [];
        if (conflicts.length > 0) {
          const lines = conflicts.map((x) =>
            `${x.severity === 'hard' ? '⚠️ ' : '• '}${x.message}`,
          ).join('\n');
          const verb = c.data?.has_hard_conflict ? 'HARD CONFLICT' : 'Heads up';
          if (!window.confirm(
            `${verb} for ${c.data?.employee_name || 'this employee'}:\n\n${lines}\n\nAssign anyway?`
          )) {
            return; // admin cancelled
          }
        }
      } catch (e) {
        // Conflict check is best-effort — proceed with assignment if it fails
        console.warn('conflict check failed', e);
      }
    }
    setSavingBookedBy(true);
    try {
      const res = await axios.patch(
        `${API_URL}/api/admin/bookings/${bookingId}/booked-by`,
        { employee_id: employeeId || null },
        { withCredentials: true }
      );
      setSelectedBooking((prev) => prev ? ({
        ...prev,
        booking: {
          ...prev.booking,
          booked_by_employee_id: res.data.booked_by_employee_id,
          booked_by_employee_name: res.data.booked_by_employee_name,
        }
      }) : prev);
      toast.success(employeeId ? `Assigned to ${res.data.booked_by_employee_name} · they were notified` : 'Reset to self-service');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSavingBookedBy(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bookingsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/bookings`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/dashboard/stats`, { withCredentials: true })
      ]);
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Unified-calendar live updates — refetch bookings/stats whenever any other
  // admin, employee, or customer triggers a booking/shift change anywhere in
  // the app. Keeps the admin dashboard in sync without manual refresh.
  // crm_changed covers deletions (bookings + customers + employees).
  useMessageStream(useCallback((evt) => {
    if (!evt) return;
    if (evt.type === 'calendar_changed') fetchData();
    if (evt.type === 'crm_changed' && ['bookings', 'customers', 'employees'].includes(evt.section)) {
      fetchData();
    }
  }, []));

  const fetchFilteredBookings = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (lifecycleFilter && lifecycleFilter !== 'all') params.append('lifecycle_status', lifecycleFilter);
      if (paymentFilter && paymentFilter !== 'all') params.append('payment_status', paymentFilter);
      if (packageFilter && packageFilter !== 'all') params.append('package_id', packageFilter);
      if (dateFilter) params.append('date_from', dateFilter.toISOString().split('T')[0]);
      // Respect a URL-driven date_to (used by "Today" / "Next 7 Days" summary cards)
      const urlDateTo = searchParams.get('date_to');
      if (urlDateTo) params.append('date_to', urlDateTo);
      if (outstandingOnly) params.append('outstanding', 'true');
      if (balanceDueOnly) params.append('balance_due', 'true');

      const response = await axios.get(`${API_URL}/api/admin/bookings?${params.toString()}`, { 
        withCredentials: true 
      });
      setBookings(response.data);
    } catch (error) {
      toast.error('Failed to filter bookings');
    }
  };

  // Apply URL-driven filters on first mount so deep-links from CrmOverview cards work.
  useEffect(() => {
    const hasAnyUrlFilter =
      searchParams.get('status') || searchParams.get('payment_status') ||
      searchParams.get('date_from') || searchParams.get('outstanding') || searchParams.get('balance_due');
    if (hasCrmAccess && hasAnyUrlFilter) {
      fetchFilteredBookings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCrmAccess]);

  // Keep the `view` query param in sync with the selected tab so URLs are shareable.
  useEffect(() => {
    const current = searchParams.get('view');
    if (activeTab === 'byDate' && current !== 'byDate') {
      searchParams.set('view', 'byDate');
      setSearchParams(searchParams, { replace: true });
    } else if (activeTab !== 'byDate' && current === 'byDate') {
      searchParams.delete('view');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Group bookings by date for the List View accordion (upcoming only, ascending)
  const groupedByDate = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = (bookings || []).filter(
      (b) => b.status !== 'cancelled' && b.status !== 'deleted' && !b.deleted_at && (b.date || '') >= today
    );
    const byDate = {};
    for (const b of upcoming) {
      const key = b.date || 'unknown';
      if (!byDate[key]) byDate[key] = [];
      byDate[key].push(b);
    }
    // Sort each day's bookings by time_slot
    Object.values(byDate).forEach((arr) => arr.sort((x, y) => (x.time_slot || '').localeCompare(y.time_slot || '')));
    // Return sorted array of [date, bookings]
    return Object.entries(byDate).sort((a, b) => a[0].localeCompare(b[0]));
  }, [bookings]);

  const toggleDateExpanded = (d) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(d)) next.delete(d); else next.add(d);
      return next;
    });
  };
  const expandAll  = () => setExpandedDates(new Set(groupedByDate.map(([d]) => d)));
  const collapseAll = () => setExpandedDates(new Set());

  const fetchAllergyReports = async (date = null) => {
    try {
      let url = `${API_URL}/api/admin/allergy-reports`;
      if (date) {
        url += `?date=${date}`;
      }
      const response = await axios.get(url, { withCredentials: true });
      setAllergyReports(response.data);
    } catch (error) {
      toast.error('Failed to load allergy reports');
    }
  };

  const fetchBookingDetails = async (bookingId) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/bookings/${bookingId}`, { 
        withCredentials: true 
      });
      setSelectedBooking(response.data);
      setDetailsModalOpen(true);
    } catch (error) {
      toast.error('Failed to load booking details');
    }
  };

  const exportBookings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/export/bookings`, { 
        withCredentials: true 
      });
      
      // Convert to CSV
      const bookingsData = response.data.bookings;
      if (bookingsData.length === 0) {
        toast.error('No bookings to export');
        return;
      }

      const headers = Object.keys(bookingsData[0]).join(',');
      const rows = bookingsData.map(b => Object.values(b).map(v => 
        typeof v === 'string' ? `"${v}"` : v
      ).join(','));
      const csv = [headers, ...rows].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bookings_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export bookings');
    }
  };

  useEffect(() => {
    if (hasCrmAccess) {
      fetchFilteredBookings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, lifecycleFilter, paymentFilter, packageFilter, dateFilter, searchParams, hasCrmAccess]);

  // Cancelled & expired-hold bookings are hidden from the default "All" view;
  // they surface only when their specific status filter is selected.
  const isHiddenWhenAll = (b) => {
    if (b.status === 'cancelled' || b.status === 'deleted' || b.deleted_at) return true;
    if (b.status === 'pending_hold' && b.hold_expires_at && new Date(b.hold_expires_at).getTime() < Date.now()) return true;
    return false;
  };
  const filteredBookings = bookings.filter(b => 
    searchTerm === '' || 
    b.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.child_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.customer_email?.toLowerCase().includes(searchTerm.toLowerCase())
  ).filter(b => (statusFilter === 'all' ? !isHiddenWhenAll(b) : true));
  // Pagination — reset to page 1 whenever filters change so the user always
  // lands on the head of the matched list.
  useEffect(() => { setBookingsPage(1); }, [searchTerm, statusFilter, lifecycleFilter, paymentFilter, packageFilter, dateFilter, bookingsPageSize]);
  const pagedBookings = filteredBookings.slice(
    (bookingsPage - 1) * bookingsPageSize,
    bookingsPage * bookingsPageSize,
  );
  const totalBookingPages = Math.max(1, Math.ceil(filteredBookings.length / bookingsPageSize));
  // Fetch signed-waiver counts for the bookings currently on screen (drives the Waivers column).
  const pagedBookingIds = pagedBookings.map((b) => b.booking_id).join(',');
  useEffect(() => {
    const ids = pagedBookingIds;
    if (!ids) { return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/waivers/form-counts`, {
          params: { booking_ids: ids }, withCredentials: true,
        });
        if (!cancelled) {
          const counts = res.data?.counts || {};
          setWaiverMap((prev) => {
            const next = { ...prev };
            ids.split(',').forEach((id) => { next[id] = { count: counts[id] || 0 }; });
            return next;
          });
        }
      } catch (_) { /* non-blocking */ }
    })();
    return () => { cancelled = true; };
  }, [pagedBookingIds]);
  // If a deletion drops the visible count below the current page, slide back.
  useEffect(() => {
    if (bookingsPage > totalBookingPages) setBookingsPage(totalBookingPages);
  }, [bookingsPage, totalBookingPages]);

  // -------- Clickable summary-card handlers --------
  // Each card click clears unrelated filters, applies the right one,
  // syncs the URL (so deep-links work), and switches to the List tab.
  const [revenueModalOpen, setRevenueModalOpen] = useState(false);

  const _resetSecondaryFilters = () => {
    setSearchTerm('');
    setPaymentFilter('all');
    setPackageFilter('all');
    setOutstandingOnly(false);
    setBalanceDueOnly(false);
  };

  const handleCardTotal = () => {
    _resetSecondaryFilters();
    setStatusFilter('all');
    setDateFilter(null);
    setActiveTab('list');
    setSearchParams({}, { replace: true });
  };

  const handleCardStatus = (statusValue) => {
    _resetSecondaryFilters();
    setStatusFilter(statusValue);
    setDateFilter(null);
    setActiveTab('list');
    setSearchParams({ status: statusValue }, { replace: true });
  };

  const handleCardToday = () => {
    _resetSecondaryFilters();
    setStatusFilter('all');
    const today = new Date();
    setDateFilter(today);
    setActiveTab('list');
    const iso = today.toISOString().split('T')[0];
    setSearchParams({ date_from: iso, date_to: iso }, { replace: true });
  };

  const handleCardNext7 = () => {
    _resetSecondaryFilters();
    setStatusFilter('all');
    const today = new Date();
    setDateFilter(today);
    setActiveTab('list');
    const fromIso = today.toISOString().split('T')[0];
    const to = new Date(today); to.setDate(to.getDate() + 7);
    const toIso = to.toISOString().split('T')[0];
    setSearchParams({ date_from: fromIso, date_to: toIso }, { replace: true });
  };

  const handleCardRevenue = () => {
    setRevenueModalOpen(true);
  };

  const getStatusBadge = (status, booking) => {
    // Auto-detected canonical lifecycle status wins when present (colour-coded
    // + note tooltip). Falls back to the legacy raw-status pill.
    if (booking?.lifecycle_status) {
      return (
        <BookingStatusBadge
          status={booking.lifecycle_status}
          note={booking.status_note}
          testid={`booking-lifecycle-${booking?.booking_id}`}
        />
      );
    }
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {status}
      </span>
    );
  };

  const getPaymentBadge = (status) => {
    const styles = {
      pending: 'bg-red-100 text-red-800',
      deposit_paid: 'bg-yellow-100 text-yellow-800',
      fully_paid: 'bg-green-100 text-green-800'
    };
    const labels = {
      pending: 'Pending',
      deposit_paid: 'Deposit Paid',
      fully_paid: 'Fully Paid'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (authLoading || loading) {
    return (
      <div className={insideCrmLayout ? '' : 'min-h-screen bg-[#FFFDF8]'}>
        {!insideCrmLayout && <Header />}
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={insideCrmLayout ? '' : 'min-h-screen bg-[#FFFDF8]'}>
      {!insideCrmLayout && <Header />}

      <div className={insideCrmLayout ? '' : 'container mx-auto px-4 py-8'}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Admin Dashboard
            </h1>
            <p className="text-[#6B6B7A]">Manage bookings, view reports, and send reminders</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={fetchData}
              className="rounded-full border-[#E8E5DF]"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={() => setPosOpen(true)}
              variant="outline"
              className="rounded-full border-[#FF3B6B] text-[#FF3B6B] hover:bg-[#FFEDF3]"
              data-testid="dashboard-take-payment-btn"
            >
              <Wallet className="w-4 h-4 mr-2" />
              Take Payment
            </Button>
            <Button
              onClick={() => setManualBookingOpen(true)}
              className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full"
              data-testid="new-manual-booking-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Manual Booking
            </Button>
            <Button
              onClick={exportBookings}
              className="bg-[#06D6A0] hover:bg-[#05B88A] text-white rounded-full"
              data-testid="export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <motion.button
              type="button"
              onClick={handleCardTotal}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl border border-[#E8E5DF] p-4 text-left hover:border-[#FF3B6B] hover:shadow-md transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#FF3B6B]/40"
              data-testid="card-total-bookings"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#FF3B6B]/10 flex items-center justify-center">
                  <CalendarIcon className="w-5 h-5 text-[#FF3B6B]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1E1E24]" data-testid="stat-total">{stats.total_bookings}</p>
                  <p className="text-xs text-[#6B6B7A]">Total Bookings</p>
                </div>
              </div>
            </motion.button>

            <motion.button
              type="button"
              onClick={() => handleCardStatus('pending_payment')}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl border border-[#E8E5DF] p-4 text-left hover:border-yellow-400 hover:shadow-md transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-yellow-300/60"
              data-testid="card-pending"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1E1E24]">{stats.pending_bookings}</p>
                  <p className="text-xs text-[#6B6B7A]">Pending</p>
                </div>
              </div>
            </motion.button>

            <motion.button
              type="button"
              onClick={() => handleCardStatus('confirmed')}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl border border-[#E8E5DF] p-4 text-left hover:border-[#06D6A0] hover:shadow-md transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#06D6A0]/40"
              data-testid="card-confirmed"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#06D6A0]/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#06D6A0]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1E1E24]">{stats.confirmed_bookings}</p>
                  <p className="text-xs text-[#6B6B7A]">Confirmed</p>
                </div>
              </div>
            </motion.button>

            <motion.button
              type="button"
              onClick={handleCardToday}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl border border-[#E8E5DF] p-4 text-left hover:border-blue-400 hover:shadow-md transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-300/40"
              data-testid="card-today"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <CalendarIcon className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1E1E24]">{stats.today_bookings}</p>
                  <p className="text-xs text-[#6B6B7A]">Today</p>
                </div>
              </div>
            </motion.button>

            <motion.button
              type="button"
              onClick={handleCardNext7}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-2xl border border-[#E8E5DF] p-4 text-left hover:border-purple-400 hover:shadow-md transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-300/40"
              data-testid="card-next-7-days"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <CalendarIcon className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1E1E24]">{stats.upcoming_bookings}</p>
                  <p className="text-xs text-[#6B6B7A]">Next 7 Days</p>
                </div>
              </div>
            </motion.button>

            <motion.button
              type="button"
              onClick={handleCardRevenue}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-2xl border border-[#E8E5DF] p-4 text-left hover:border-[#FFD166] hover:shadow-md transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#FFD166]/60"
              data-testid="card-revenue"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#FFD166]/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-[#B8860B]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1E1E24]" data-testid="stat-revenue">
                    ${stats.total_revenue?.toFixed(0) || 0}
                  </p>
                  <p className="text-xs text-[#6B6B7A]">Revenue</p>
                </div>
              </div>
            </motion.button>
          </div>
        )}

        {/* Main Content */}
        <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="border-b border-[#E8E5DF] p-4">
              <TabsList className="grid w-full max-w-2xl grid-cols-5">
                <TabsTrigger value="list" className="data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white" data-testid="tab-list">
                  <List className="w-4 h-4 mr-2" />
                  List
                </TabsTrigger>
                <TabsTrigger value="byDate" className="data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white" data-testid="tab-by-date">
                  <CalendarDays className="w-4 h-4 mr-2" />
                  By Date
                </TabsTrigger>
                <TabsTrigger value="fullcalendar" className="data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white" data-testid="tab-calendar">
                  <CalendarDays className="w-4 h-4 mr-2" />
                  Calendar
                </TabsTrigger>
                <TabsTrigger value="calendar" className="data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white" data-testid="tab-picker">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  Picker
                </TabsTrigger>
                <TabsTrigger value="allergies" className="data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white" data-testid="tab-allergies">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Allergies
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Full Calendar View */}
            <TabsContent value="fullcalendar" className="p-6">
              <AdminCalendarView 
                onBookingClick={fetchBookingDetails}
                onSendReminder={(booking, channel) => openBookingMessage(booking, channel || 'email')}
              />
            </TabsContent>

            {/* List View */}
            <TabsContent value="list" className="p-0">
              {/* Filters */}
              <div className="p-4 border-b border-[#E8E5DF] bg-[#FFFDF8]">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B7A]" />
                    <Input
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 rounded-xl border-[#E8E5DF]"
                      data-testid="search-input"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="rounded-xl border-[#E8E5DF]" data-testid="bookings-status-filter">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      {/* iter-136 — show all Pay-Later holds (active + expired) */}
                      <SelectItem value="pending_hold" data-testid="filter-temp-hold">Temp Hold (Pay-Later)</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                    <SelectTrigger className="rounded-xl border-[#E8E5DF]">
                      <SelectValue placeholder="Payment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Payments</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="deposit_paid">Deposit Paid</SelectItem>
                      <SelectItem value="fully_paid">Fully Paid</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={packageFilter} onValueChange={setPackageFilter}>
                    <SelectTrigger className="rounded-xl border-[#E8E5DF]">
                      <SelectValue placeholder="Package" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Packages</SelectItem>
                      <SelectItem value="create">Create Party</SelectItem>
                      <SelectItem value="celebrate">Celebrate Party</SelectItem>
                      <SelectItem value="signature">Signature Edible</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setStatusFilter('all');
                      setLifecycleFilter('all');
                      setPaymentFilter('all');
                      setPackageFilter('all');
                      setSearchTerm('');
                    }}
                    className="rounded-xl border-[#E8E5DF]"
                  >
                    Clear Filters
                  </Button>
                </div>
                {/* Auto-detected status filter chips */}
                <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid="lifecycle-filter-chips">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-[#6B6B7A] mr-1">Status:</span>
                  <button
                    type="button"
                    onClick={() => setLifecycleFilter('all')}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${lifecycleFilter === 'all' ? 'bg-[#1E1E24] text-white border-[#1E1E24]' : 'bg-white text-[#6B6B7A] border-[#E8E5DF] hover:border-[#B7B3AC]'}`}
                    data-testid="lifecycle-chip-all"
                  >
                    All
                  </button>
                  {STATUS_ORDER.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setLifecycleFilter(s)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${lifecycleFilter === s ? STATUS_META[s].badge + ' ring-2 ring-offset-1 ring-[#FF3B6B]/40' : 'bg-white text-[#6B6B7A] border-[#E8E5DF] hover:border-[#B7B3AC]'}`}
                      data-testid={`lifecycle-chip-${s}`}
                    >
                      {STATUS_META[s].label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <BulkSelectAllCheckbox
                          ids={pagedBookings.map((b) => b.booking_id)}
                          selected={bulkSelected}
                          setSelected={setBulkSelected}
                          testid="bookings-select-all"
                        />
                      </TableHead>
                      <TableHead>ID</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Waivers</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pagedBookings.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-[#6B6B7A]">
                          No bookings found
                        </TableCell>
                      </TableRow>
                    ) : (
                      pagedBookings.map((booking) => (
                        <TableRow key={booking.booking_id}>
                          <TableCell>
                            <BulkSelectCheckbox
                              id={booking.booking_id}
                              selected={bulkSelected}
                              setSelected={setBulkSelected}
                              testid={`booking-checkbox-${booking.booking_id}`}
                            />
                          </TableCell>
                          <TableCell className="font-mono text-xs font-bold text-[#FF3B6B]" data-testid={`booking-ref-${booking.booking_id}`}>
                            {booking.booking_reference || booking.booking_id?.slice(-6)}
                          </TableCell>
                          <TableCell>
                            <Link
                              to={`/crm/bookings/${booking.booking_id}`}
                              className="block hover:underline"
                              data-testid={`customer-link-${booking.booking_id}`}
                            >
                              <p className="font-medium text-[#1E1E24] truncate max-w-[200px]">{booking.customer_name}</p>
                              <p className="text-xs text-[#6B6B7A] truncate max-w-[200px]">{booking.child_name} · {booking.package_name}</p>
                            </Link>
                          </TableCell>
                          <TableCell className="text-sm">
                            <div className="font-medium">{new Date(booking.date).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}</div>
                            <div className="text-xs text-[#6B6B7A]">{booking.time_slot === '10:00' ? '10 AM' : booking.time_slot === '13:00' ? '1 PM' : '4 PM'}</div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              {getStatusBadge(booking.status, booking)}
                              {Number(booking.customer_credit_balance) > 0.005 && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#E9FBF4] text-[#058B69] border border-[#06D6A0]/30 w-fit"
                                  title="Customer has leftover overpayment credit — can be applied to any of their bookings"
                                  data-testid={`booking-credit-${booking.booking_id}`}>
                                  <Wallet className="w-2.5 h-2.5" />
                                  has ${Number(booking.customer_credit_balance).toFixed(2)} credit
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell data-testid={`booking-waivers-cell-${booking.booking_id}`}>
                            {(() => {
                              const count = waiverMap[booking.booking_id]?.count || 0;
                              return count > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => setWaiverModalBookingId(booking.booking_id)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#06D6A0]/15 text-[#0F8B5C] hover:bg-[#06D6A0]/25 transition-colors"
                                  title="View signed waiver & consent forms"
                                  data-testid={`booking-waiver-${booking.booking_id}`}
                                >
                                  <ShieldCheck className="w-3 h-3" /> {count} waiver{count === 1 ? '' : 's'}
                                </button>
                              ) : (
                                <span className="text-xs text-[#9A968C]" data-testid={`booking-waiver-${booking.booking_id}`}>—</span>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                size="sm" variant="outline"
                                onClick={() => navigate(`/crm/bookings/${booking.booking_id}`)}
                                className="rounded-full h-8 px-3 text-xs border-[#FF3B6B]/30 text-[#FF3B6B] hover:bg-[#FF3B6B] hover:text-white"
                                data-testid={`view-edit-booking-${booking.booking_id}`}
                              >
                                <Eye className="w-3.5 h-3.5 mr-1" /> View / Edit
                              </Button>
                              <ActionMenu
                                testid={`booking-actions-${booking.booking_id}`}
                                items={[
                                  { label: 'Send reminder', icon: Mail, onClick: () => openBookingMessage(booking, 'email') },
                                  { label: 'Send SMS', icon: MessageSquare, onClick: () => openBookingMessage(booking, 'sms'), disabled: !booking.customer_phone },
                                  { separator: true },
                                  { label: 'Cancel booking', icon: Trash2, destructive: true, onClick: () => navigate(`/crm/bookings/${booking.booking_id}?cancel=1`) },
                                ]}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="p-4 border-t border-[#E8E5DF]">
                <Pagination
                  page={bookingsPage}
                  pageSize={bookingsPageSize}
                  totalItems={filteredBookings.length}
                  onPageChange={setBookingsPage}
                  onPageSizeChange={setBookingsPageSize}
                  testidPrefix="bookings-pagination"
                  itemLabel="booking"
                />
              </div>
            </TabsContent>

            {/* By Date View — accordion grouped by party date */}
            <TabsContent value="byDate" className="p-0">
              <div className="p-4 border-b border-[#E8E5DF] bg-[#FFFDF8] flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    Upcoming bookings, grouped by date
                  </h3>
                  <p className="text-xs text-[#6B6B7A]">
                    {groupedByDate.length} {groupedByDate.length === 1 ? 'day' : 'days'} with bookings.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={expandAll}
                    className="rounded-full border-[#E8E5DF] text-[#1E1E24] h-9 px-4"
                    data-testid="bydate-expand-all"
                  >
                    Expand All
                  </Button>
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={collapseAll}
                    className="rounded-full border-[#E8E5DF] text-[#1E1E24] h-9 px-4"
                    data-testid="bydate-collapse-all"
                  >
                    Collapse All
                  </Button>
                </div>
              </div>

              {groupedByDate.length === 0 ? (
                <div className="p-10 text-center">
                  <CalendarDays className="w-10 h-10 text-[#9a9aa6] mx-auto mb-3" />
                  <p className="text-[#6B6B7A]">No upcoming bookings. Enjoy the quiet moment.</p>
                </div>
              ) : (
                <div className="divide-y divide-[#E8E5DF]" data-testid="bydate-list">
                  {groupedByDate.map(([date, rows]) => {
                    const expanded = expandedDates.has(date);
                    const dayLabel = new Date(`${date}T00:00:00`).toLocaleDateString('en-CA', {
                      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                    });
                    return (
                      <div key={date} data-testid={`bydate-group-${date}`}>
                        <button
                          type="button"
                          onClick={() => toggleDateExpanded(date)}
                          className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-[#FFF8E7]/50 transition-colors text-left"
                          data-testid={`bydate-header-${date}`}
                          aria-expanded={expanded}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            {expanded ? (
                              <ChevronDown className="w-5 h-5 text-[#FF3B6B] flex-shrink-0" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-[#6B6B7A] flex-shrink-0" />
                            )}
                            <span className="font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                              {dayLabel}
                            </span>
                            <span className="text-xs text-[#6B6B7A]">
                              ({rows.length} {rows.length === 1 ? 'booking' : 'bookings'})
                            </span>
                          </div>
                        </button>
                        {expanded && (
                          <div className="overflow-x-auto bg-white" data-testid={`bydate-table-${date}`}>
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-[#FFFDF8] hover:bg-[#FFFDF8]">
                                  <TableHead className="text-[#6B6B7A] text-xs">Time</TableHead>
                                  <TableHead className="text-[#6B6B7A] text-xs">Customer</TableHead>
                                  <TableHead className="text-[#6B6B7A] text-xs">Child</TableHead>
                                  <TableHead className="text-[#6B6B7A] text-xs">Package</TableHead>
                                  <TableHead className="text-[#6B6B7A] text-xs">Status</TableHead>
                                  <TableHead className="text-[#6B6B7A] text-xs text-right">Action</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {rows.map((b) => (
                                  <TableRow key={b.booking_id} data-testid={`bydate-row-${b.booking_id}`}>
                                    <TableCell className="text-sm font-medium text-[#1E1E24]">{b.time_slot}</TableCell>
                                    <TableCell className="text-sm text-[#1E1E24]">
                                      {b.customer_name}
                                      {Number(b.customer_credit_balance) > 0.005 && (
                                        <span className="mt-0.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#E9FBF4] text-[#058B69] border border-[#06D6A0]/30 w-fit"
                                          title="Customer has leftover overpayment credit — can be applied to any of their bookings"
                                          data-testid={`bydate-credit-${b.booking_id}`}>
                                          <Wallet className="w-2.5 h-2.5" />
                                          has ${Number(b.customer_credit_balance).toFixed(2)} credit
                                        </span>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-sm text-[#1E1E24]">{b.child_name} ({b.child_age})</TableCell>
                                    <TableCell className="text-sm text-[#1E1E24]">{b.package_name}</TableCell>
                                    <TableCell>
                                      {(() => {
                                        // iter-136 — distinguish active vs expired Pay-Later holds
                                        const isHold = b.status === 'pending_hold';
                                        const expiresAt = b.hold_expires_at ? new Date(b.hold_expires_at) : null;
                                        const expired = isHold && expiresAt && expiresAt.getTime() < Date.now();
                                        return (
                                          <>
                                            <span
                                              className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
                                              style={{
                                                backgroundColor:
                                                  b.status === 'confirmed' ? '#06D6A020' :
                                                  expired ? '#D4302A20' :
                                                  isHold ? '#FF7A2E20' :
                                                  b.status === 'pending' ? '#FFD16620' : '#E8E5DF',
                                                color:
                                                  b.status === 'confirmed' ? '#05a07a' :
                                                  expired ? '#D4302A' :
                                                  isHold ? '#FF7A2E' :
                                                  b.status === 'pending' ? '#b38600' : '#6B6B7A',
                                              }}
                                              title={isHold && expiresAt ? `Hold expires ${expiresAt.toLocaleString()}` : undefined}
                                              data-testid={`booking-status-${b.booking_id}${expired ? '-expired' : ''}`}
                                            >
                                              {expired ? 'EXPIRED' : isHold ? 'HOLD' : b.status}
                                            </span>
                                            {isHold && expiresAt && (
                                              expired ? (
                                                <div className="text-[10px] text-[#D4302A] mt-0.5 font-medium" data-testid={`hold-expired-at-${b.booking_id}`}>
                                                  Expired {expiresAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' })}
                                                </div>
                                              ) : (
                                                <div className="text-[10px] text-[#FF7A2E] mt-0.5" data-testid={`hold-expires-${b.booking_id}`}>
                                                  Expires {expiresAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' })}
                                                </div>
                                              )
                                            )}
                                          </>
                                        );
                                      })()}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <Button
                                        size="sm" variant="outline"
                                        onClick={() => fetchBookingDetails(b.booking_id)}
                                        className="rounded-full h-7 px-3 text-xs border-[#FF3B6B]/30 text-[#FF3B6B] hover:bg-[#FF3B6B] hover:text-white"
                                        data-testid={`bydate-view-${b.booking_id}`}
                                      >
                                        <Eye className="w-3 h-3 mr-1" /> View
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* Calendar View */}
            <TabsContent value="calendar" className="p-6">
              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={dateFilter}
                  onSelect={(date) => {
                    setDateFilter(date);
                    if (date) {
                      fetchAllergyReports(date.toISOString().split('T')[0]);
                    }
                  }}
                  className="rounded-xl border border-[#E8E5DF]"
                />
              </div>
              {dateFilter && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold mb-4">
                    Bookings for {dateFilter.toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </h3>
                  <div className="space-y-3">
                    {filteredBookings.filter(b => b.date === dateFilter.toISOString().split('T')[0]).map(booking => (
                      <div 
                        key={booking.booking_id}
                        className="p-4 bg-[#FFFDF8] rounded-xl border border-[#E8E5DF]"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold">{booking.child_name}'s Party</p>
                            <p className="text-sm text-[#6B6B7A]">
                              {booking.time_slot === '10:00' ? '10:00 AM' : booking.time_slot === '13:00' ? '1:00 PM' : '4:00 PM'} | {booking.package_name}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {getStatusBadge(booking.status, booking)}
                          </div>
                        </div>
                      </div>
                    ))}
                    {filteredBookings.filter(b => b.date === dateFilter.toISOString().split('T')[0]).length === 0 && (
                      <p className="text-[#6B6B7A] text-center py-4">No bookings for this date</p>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Allergy Reports */}
            <TabsContent value="allergies" className="p-6">
              <div className="mb-6">
                <Button
                  onClick={() => fetchAllergyReports()}
                  className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full"
                  data-testid="load-allergies-btn"
                >
                  Load All Allergy Reports
                </Button>
              </div>
              
              {allergyReports.length === 0 ? (
                <p className="text-[#6B6B7A] text-center py-8">No allergy reports found. Click the button above to load reports.</p>
              ) : (
                <div className="space-y-4">
                  {allergyReports.map((report, index) => (
                    <div 
                      key={index}
                      className="p-4 bg-[#FFFDF8] rounded-xl border border-[#E8E5DF]"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-semibold text-[#1E1E24]">{report.child_name}</p>
                          <p className="text-sm text-[#6B6B7A]">
                            Party: {report.party_date} at {report.party_time}
                          </p>
                        </div>
                      </div>
                      
                      {report.medical_data?.allergies?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-sm font-semibold text-red-600 mb-1">Allergies:</p>
                          <div className="flex flex-wrap gap-2">
                            {report.medical_data.allergies.map((allergy, i) => (
                              <span key={i} className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                                {allergy}
                              </span>
                            ))}
                            {report.medical_data.allergy_other && (
                              <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                                {report.medical_data.allergy_other}
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {report.medical_data?.medical_conditions?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-sm font-semibold text-yellow-600 mb-1">Medical Conditions:</p>
                          <div className="flex flex-wrap gap-2">
                            {report.medical_data.medical_conditions.map((condition, i) => (
                              <span key={i} className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                                {condition}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="text-sm text-[#6B6B7A]">
                        <p>Emergency Contact: {report.emergency_contact_name} ({report.emergency_contact_relationship})</p>
                        <p>Phone: {report.emergency_contact_phone}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Revenue Breakdown Modal */}
        <Dialog open={revenueModalOpen} onOpenChange={setRevenueModalOpen}>
          <DialogContent className="max-w-lg" data-testid="revenue-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                <DollarSign className="w-5 h-5 text-[#B8860B]" /> Revenue Breakdown
              </DialogTitle>
            </DialogHeader>
            {stats?.revenue_breakdown ? (
              <div className="space-y-3 mt-2">
                {[
                  { key: 'today', label: 'Today', sub: stats.revenue_breakdown.today?.date, accent: '#118AB2' },
                  { key: 'this_week', label: 'This Week', sub: `${stats.revenue_breakdown.this_week?.start} → ${stats.revenue_breakdown.this_week?.end}`, accent: '#6366F1' },
                  { key: 'this_month', label: 'This Month', sub: `${stats.revenue_breakdown.this_month?.start} → ${stats.revenue_breakdown.this_month?.end}`, accent: '#FF3B6B' },
                  { key: 'all_time', label: 'All Time (Net)', sub: 'Total received across all methods, minus refunds', accent: '#06D6A0' },
                ].map((row) => {
                  const r = stats.revenue_breakdown[row.key] || {};
                  return (
                    <div key={row.key}
                         className="flex items-center justify-between rounded-2xl border border-[#E8E5DF] bg-white p-3"
                         data-testid={`revenue-row-${row.key}`}>
                      <div className="min-w-0">
                        <p className="text-xs uppercase tracking-wide text-[#6B6B7A]">{row.label}</p>
                        <p className="text-[10px] text-[#6B6B7A] truncate">{row.sub}</p>
                        {r.count != null && <p className="text-[10px] text-[#6B6B7A]">{r.count} {r.count === 1 ? 'booking' : 'bookings'}</p>}
                      </div>
                      <p className="text-2xl font-bold whitespace-nowrap" style={{ color: row.accent, fontFamily: 'Fredoka, sans-serif' }}>
                        ${Number(r.amount || 0).toFixed(2)}
                      </p>
                    </div>
                  );
                })}
                <div className="rounded-xl bg-[#FFFDF8] border border-[#E8E5DF] px-3 py-2 text-[11px] text-[#6B6B7A]">
                  <b>Note:</b> Revenue is summed from the unified payments ledger (<code>db.payments</code>) — counts every Stripe, PayPal, Cash and e-Transfer dollar actually received, minus refunds. Matches the P&L exactly.
                </div>

                {/* iter-139 — Revenue by method (this month) */}
                {stats.revenue_breakdown.by_method_this_month && (
                  <div className="rounded-2xl border border-[#E8E5DF] bg-white p-3" data-testid="revenue-by-method-block">
                    <p className="text-xs uppercase tracking-wide text-[#6B6B7A] mb-2">Revenue by Method · This Month</p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { key: 'cash', label: 'Cash', accent: '#FFD166' },
                        { key: 'etransfer', label: 'e-Transfer', accent: '#06D6A0' },
                        { key: 'credit_card_online', label: 'Stripe Online', accent: '#FF3B6B' },
                        { key: 'credit_card_terminal', label: 'Stripe Terminal', accent: '#118AB2' },
                        { key: 'debit_card_terminal', label: 'Debit Terminal', accent: '#6366F1' },
                        { key: 'paypal', label: 'PayPal', accent: '#0070BA' },
                        { key: 'cheque', label: 'Cheque', accent: '#8B6F47' },
                        { key: 'gift_card', label: 'Gift Card', accent: '#EF476F' },
                      ].map((m) => {
                        const r = stats.revenue_breakdown.by_method_this_month[m.key] || { amount: 0, count: 0 };
                        return (
                          <div key={m.key}
                               className="flex items-center justify-between bg-[#FFFDF8] rounded-xl border border-[#E8E5DF] px-3 py-2"
                               data-testid={`revenue-method-${m.key}`}>
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-[#1E1E24]">{m.label}</p>
                              <p className="text-[10px] text-[#6B6B7A]">{r.count || 0} txn{r.count === 1 ? '' : 's'}</p>
                            </div>
                            <p className="text-base font-bold" style={{ color: m.accent, fontFamily: 'Fredoka, sans-serif' }}>
                              ${Number(r.amount || 0).toFixed(2)}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="outline" onClick={() => { setRevenueModalOpen(false); navigate('/crm/analytics'); }} className="rounded-full text-xs" data-testid="revenue-analytics-btn">
                    Full Analytics →
                  </Button>
                  <Button onClick={() => setRevenueModalOpen(false)} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white text-xs" data-testid="revenue-close-btn">
                    Close
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-[#6B6B7A] py-6 text-center">Loading…</p>
            )}
          </DialogContent>
        </Dialog>

        {/* Booking Details Modal */}
        <Dialog open={detailsModalOpen} onOpenChange={setDetailsModalOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Fredoka, sans-serif' }}>Booking Details</DialogTitle>
            </DialogHeader>
            {selectedBooking && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-[#6B6B7A]">Booking ID</p>
                    <p className="font-medium">{selectedBooking.booking?.booking_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#6B6B7A]">Package</p>
                    <p className="font-medium">{selectedBooking.booking?.package_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#6B6B7A]">Date & Time</p>
                    <p className="font-medium">
                      {selectedBooking.booking?.date} at {selectedBooking.booking?.time_slot}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-[#6B6B7A]">Guests</p>
                    <p className="font-medium">{selectedBooking.booking?.guest_count}</p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Customer Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-[#6B6B7A]">Name</p>
                      <p className="font-medium">{selectedBooking.booking?.customer_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#6B6B7A]">Email</p>
                      <p className="font-medium">{selectedBooking.booking?.customer_email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#6B6B7A]">Phone</p>
                      <p className="font-medium">{selectedBooking.booking?.customer_phone}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#6B6B7A]">Child</p>
                      <p className="font-medium">{selectedBooking.booking?.child_name} ({selectedBooking.booking?.child_age} yrs)</p>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Payment</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-[#6B6B7A]">Total</p>
                      <p className="font-medium">${selectedBooking.booking?.total?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#6B6B7A]">Deposit</p>
                      <p className="font-medium">${selectedBooking.booking?.deposit_amount?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#6B6B7A]">Status</p>
                      {getPaymentBadge(selectedBooking.booking?.payment_status)}
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Booked By</h4>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <p className="text-sm text-[#1E1E24]" data-testid="booked-by-label">
                      Current: <span className="font-medium">{selectedBooking.booking?.booked_by_employee_name || 'Self-service (customer)'}</span>
                    </p>
                    <Select
                      value={selectedBooking.booking?.booked_by_employee_id || 'self'}
                      onValueChange={(v) => updateBookedBy(selectedBooking.booking.booking_id, v === 'self' ? '' : v)}
                      disabled={savingBookedBy}
                    >
                      <SelectTrigger className="bg-white sm:max-w-xs" data-testid="booked-by-select">
                        <SelectValue placeholder="Assign employee" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="self">Self-service (customer)</SelectItem>
                        {employees.map((emp) => (
                          <SelectItem key={emp.user_id} value={emp.user_id}>
                            {emp.name || emp.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {(selectedBooking.booking?.custom_charges?.length > 0 || selectedBooking.booking?.add_ons?.length > 0) && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-3">Extras / Add-Ons</h4>
                    <div className="space-y-1 text-sm">
                      {(selectedBooking.booking?.add_ons || []).map((a, i) => (
                        <div key={`ao-${i}`} className="flex justify-between">
                          <span className="text-[#1E1E24]">{a.name}{a.quantity > 1 ? ` x${a.quantity}` : ''}</span>
                          <span className="text-[#6B6B7A]">${((a.price || 0) * (a.quantity || 1)).toFixed(2)}</span>
                        </div>
                      ))}
                      {(selectedBooking.booking?.custom_charges || []).map((c, i) => (
                        <div key={`cc-${i}`} className="flex justify-between">
                          <span className="text-[#6366F1]">{c.name || c.description}{c.quantity > 1 ? ` x${c.quantity}` : ''}</span>
                          <span className="text-[#6B6B7A]">${(c.amount || 0).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedBooking.allergy_form && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-3">Allergy Form</h4>
                    {selectedBooking.allergy_form.medical_data?.allergies?.length > 0 && (
                      <div className="mb-2">
                        <p className="text-sm text-red-600">Allergies: {selectedBooking.allergy_form.medical_data.allergies.join(', ')}</p>
                      </div>
                    )}
                    {selectedBooking.allergy_form.medical_data?.medical_conditions?.length > 0 && (
                      <div className="mb-2">
                        <p className="text-sm text-yellow-600">Medical: {selectedBooking.allergy_form.medical_data.medical_conditions.join(', ')}</p>
                      </div>
                    )}
                    <p className="text-sm">Emergency: {selectedBooking.allergy_form.emergency_contact_name} - {selectedBooking.allergy_form.emergency_contact_phone}</p>
                    <p className="text-sm">Photo Consent: {selectedBooking.allergy_form.photo_video_consent ? 'Yes' : 'No'}</p>
                  </div>
                )}

                {selectedBooking.booking && selectedBooking.booking.status !== 'cancelled' && (
                  <div className="border-t pt-4">
                    <Button
                      onClick={() => {
                        setCancelTarget(selectedBooking.booking);
                        setDetailsModalOpen(false);
                        setCancelOpen(true);
                      }}
                      className="w-full bg-red-500 hover:bg-red-600 text-white rounded-full"
                      data-testid="details-cancel-btn"
                    >
                      <Ban className="w-4 h-4 mr-2" /> Cancel Booking
                    </Button>
                  </div>
                )}

                {/* Unified payments panel — Take Payment / Add Charge / Refund */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>Payments</h4>
                  <BookingPaymentsPanel bookingId={selectedBooking.booking?.booking_id} />
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <CancelBookingModal
          booking={cancelTarget}
          open={cancelOpen}
          onClose={() => setCancelOpen(false)}
          onSuccess={() => { setCancelOpen(false); fetchData(); }}
        />

        <BulkDeleteBar
          section="bookings"
          selectedIds={bulkSelected}
          onClearSelection={() => setBulkSelected([])}
          onDone={() => { fetchData(); }}
          labelSingular="booking"
          dataTestPrefix="bookings-bulk"
        />

        <EditBookingDialog
          booking={editTarget}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); fetchData(); }}
          packages={packages}
          employees={employees}
        />

        <ManualBookingDialog
          open={manualBookingOpen}
          onOpenChange={setManualBookingOpen}
          onCreated={() => fetchData()}
        />

        {/* Standalone POS card charge — any amount, optional customer link */}
        <PaymentModal
          open={posOpen}
          onOpenChange={setPosOpen}
          mode="admin"
          posMode
          title="Take Payment"
          onSuccess={() => fetchData()}
        />

        {/* Send Email / SMS to customer for this booking (template picker + preview) */}
        <CustomerMessageDialog
          open={msgBookingOpen}
          onOpenChange={setMsgBookingOpen}
          booking={msgBooking}
          defaultChannel={msgBookingChannel}
        />

        {/* Waivers for a booking (opened from the Waivers column) */}
        <GuestFormsDialog
          open={!!waiverModalBookingId}
          onOpenChange={(v) => { if (!v) setWaiverModalBookingId(null); }}
          title="Signed waiver & consent forms"
          pdfUrlFor={(f) => `${API_URL}/api/admin/waivers/${f.form_id}/pdf`}
          loader={async () => {
            const res = await axios.get(`${API_URL}/api/admin/bookings/${waiverModalBookingId}/waiver-forms`, { withCredentials: true });
            return res.data;
          }}
        />
      </div>
    </div>
  );
};

export default AdminDashboard;
